/**
 * Organization mutation actions (switch, resend invitation, cancel, update).
 * Extracted from useOrganizations for the 150-line rule.
 */

import { useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
	queryKeys,
	useOrgResendInvitation,
	useOrgCancelInvitation,
	useOrgUpdateOrganization,
} from "@/application/queries";
import { ROUTES } from "@/app/routes";
import { useOrganizationStore } from "@/stores/useOrganizationStore";
import { useSwitchOrganization } from "./useSwitchOrganization";
import { useCurrentOrganizationId } from "./useCurrentOrganizationId";

/** Routes with dynamic entity params where staying after org switch would cause 404s. */
const DETAIL_ROUTE_PATTERNS = [
	/^\/promoters\/[^/]+/,
	/^\/projects\/[^/]+/,
	/^\/investments\/[^/]+/,
	/^\/organization\/templates\/[^/]+/,
];

export const useOrganizationActions = () => {
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const location = useLocation();
	const orgId = useCurrentOrganizationId();
	const setSelectedOrganizationUuid = useOrganizationStore(
		(s) => s.setSelectedOrganizationUuid,
	);

	const resendInvitationMutation = useOrgResendInvitation(orgId);
	const cancelInvitationMutation = useOrgCancelInvitation(orgId);
	const switchOrganizationMutation = useSwitchOrganization();
	const updateOrganizationMutation = useOrgUpdateOrganization(orgId);

	const loadUserOrganizations = useCallback(() => {
		queryClient.invalidateQueries({
			queryKey: queryKeys.organization.userOrganizations(),
		});
	}, [queryClient]);

	const loadOrganizationDetails = useCallback(() => {
		queryClient.invalidateQueries({
			queryKey: queryKeys.organization.details(orgId),
		});
	}, [queryClient, orgId]);

	const loadOrganizationUsers = useCallback(() => {
		queryClient.refetchQueries({
			queryKey: queryKeys.organization.users(orgId),
		});
	}, [queryClient, orgId]);

	const loadOrganizationInvitations = useCallback(() => {
		queryClient.refetchQueries({
			queryKey: queryKeys.organization.invitations(orgId),
		});
	}, [queryClient, orgId]);

	const loadOrganizationCertificates = useCallback(() => {
		queryClient.refetchQueries({
			queryKey: queryKeys.organization.certificates(orgId),
		});
	}, [queryClient, orgId]);

	const loadOrganizationTemplates = useCallback(() => {
		queryClient.refetchQueries({
			queryKey: queryKeys.organization.templates(orgId),
		});
	}, [queryClient, orgId]);

	const dispatchSwitchOrganization = useCallback(
		async (organizationId: string | number) => {
			try {
				const result =
					await switchOrganizationMutation.mutateAsync(organizationId);
				setSelectedOrganizationUuid(String(organizationId));

				// Navigate away from detail pages to avoid refetching entity data
				// that doesn't exist in the new organization
				const isDetailRoute = DETAIL_ROUTE_PATTERNS.some((p) =>
					p.test(location.pathname),
				);
				if (isDetailRoute) {
					navigate(ROUTES.DASHBOARD, { replace: true });
				}

				return result;
			} catch {
				return;
			}
		},
		[
			switchOrganizationMutation,
			setSelectedOrganizationUuid,
			location.pathname,
			navigate,
		],
	);

	const dispatchResendInvitation = useCallback(
		async (invitationUuid: string) => {
			try {
				return await resendInvitationMutation.mutateAsync(invitationUuid);
			} catch {
				return;
			}
		},
		[resendInvitationMutation],
	);

	const dispatchCancelInvitation = useCallback(
		async (invitationUuid: string) => {
			try {
				return await cancelInvitationMutation.mutateAsync(invitationUuid);
			} catch {
				return;
			}
		},
		[cancelInvitationMutation],
	);

	const dispatchUpdateOrganization = useCallback(
		async (
			data: Parameters<typeof updateOrganizationMutation.mutateAsync>[0],
		) => {
			try {
				return await updateOrganizationMutation.mutateAsync(data);
			} catch {
				return;
			}
		},
		[updateOrganizationMutation],
	);

	return {
		isUpdatingOrganization: updateOrganizationMutation.isPending,
		loadUserOrganizations,
		loadOrganizationDetails,
		loadOrganizationUsers,
		loadOrganizationInvitations,
		loadOrganizationCertificates,
		loadOrganizationTemplates,
		switchOrganization: dispatchSwitchOrganization,
		resendInvitation: dispatchResendInvitation,
		cancelInvitation: dispatchCancelInvitation,
		updateOrganization: dispatchUpdateOrganization,
	};
};
